import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import { createContext, ReactNode, useContext, useState } from "react";
import { uiConfigOptionsAttributes } from "../types";
import OrderConfiguration from "../features/orderConfiguration/OrderConfiguration";
import RenderSvg from "../components/renderSvg/RenderSvg";
import { useTranslation } from "react-i18next";
import { changeKeyToSnakeCase } from "../services/translationService/translationService";

export type stringMapping = {
  [key: string]: string;
};
interface TranslationContextType {
  translate: (
    componentName: string,
    translationKey: string,
    dynamicParams?: stringMapping
  ) => string;
}

const TranslationContext = createContext<TranslationContextType>(
  {} as TranslationContextType
);

const TranslationProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  function translate(
    componentName: string,
    translationName: string,
    dynamicParams?: stringMapping
  ) {
    const translationKey = changeKeyToSnakeCase(translationName);
    let translatedValue = t(`${componentName}.${translationKey}`, {
      defaultValue: translationName,
    });
    if (dynamicParams) {
      Object.keys(dynamicParams).forEach((paramKey: string) => {
        const paramValue = dynamicParams[paramKey];
        const paramTranslationKey = changeKeyToSnakeCase(paramValue) ?? "";
        const paramTranslationValue = t(
          `${componentName}.${paramTranslationKey}`,
          { defaultValue: paramValue }
        );
        translatedValue = translatedValue.replaceAll(
          `{${paramKey}}`,
          paramTranslationValue
        );
      });
    }
    return translatedValue;
  }
  return (
    <TranslationContext.Provider value={{ translate }}>
      {children}
    </TranslationContext.Provider>
  );
};

export function usePortalTranslation(): TranslationContextType {
  return useContext(TranslationContext);
}

export default TranslationProvider;

export enum TranslationEnum {
    ui_config = 'UiConfig',
    order = 'order',
    homepage = 'homepage',
    common = 'common',
    login = 'login',
    notification = 'notification',
    dashboard = 'dashboard',
    sellina = 'sellina',
    inventory = 'inventory',
    survey = 'survey',
    pjp = 'pjp',
    faq = 'faq',
    helpdesk = 'helpdesk',
    outletMapping = 'outletMapping',
    attendance = 'attendance',
    profile = 'profile',
    stockReturn = 'stockReturn',
    petRegistration = 'petRegistration',
    outletActivity = 'outletActivity',
    mustDoAction = 'mustDoAction',
    baskets = 'Baskets',
    login_page = 'Login Page',
    catalogue = 'Catalogue',
    complaint = 'Complaint',
    cart = 'Cart',
    order_summary = 'Order Summary',
    order_tracking = 'Order Tracking',
    manage_banner = 'Manage Banner',
    admin_panel = 'Admin Panel',
    report_and_logs = 'Report And Logs',
    common_component = 'Common Component',
    generic_filters = 'Generic Filters',
    manage_bucket = 'Manage Bucket',
    common_portal = 'commonPortal',
    smart_trigger = 'Smart Trigger',
    support = 'Support',
    manage_basket = 'Manage Basket',
    update_configuration = 'Update Configuration',
    firebase_configuration = 'Firebase Configuration',
    update_translation = 'Update Translation',
    manage_policies = 'Manage Policies',
    manage_events = 'Manage Events',
    manage_update_access = 'Manage Update Access',
    master_data_management = 'Master Data Management',
    division = 'Division',
    import = 'Import',
    validation_support = 'ValidationSupport',
    validate_api = 'Validate API',
    user = 'User',
    outlet_details = 'OutletDetails',
    product_details = 'ProductDetails',
    blobkey = 'Blobkey',
    order_analytics = 'Order Analytics',
    reports = 'Reports',
    manage_order = 'Manage Order',
    analytics_dashboard = 'Analytics Dashboard',
    master_data_reports = 'Master Data Reports',
    slow_queries = 'Slow Queries',
    validation_division = 'ValidationDivision',
    generic_modal = 'GenericModal',
    sidebar_options = 'Sidebar Options',
    manage_registry = 'Manage Registry',
    manage_home_screen = 'Manage Home Screen',
    existing_outlet_approval = 'Existing Outlet Approval',
    outlet_approval = 'Outlet Approval',
    navbar_options = 'Navbar Options',
    business_impact = 'Business Impact',
    integration_statistics = 'Integration Statistics',
    historical_cleanup = 'Historical Datacleanup',
    login_and_change_password = 'LogInAndChangePassword',
    validate_otp = 'ValidateOtp',
    password_reset = 'PasswordReset',
    analytics = 'Analytics',
    language = 'Language',
    manage_feedbacks = 'ManageFeedbacks',
    pjp_approval = 'Pjp Approval',
    deviation_approval = 'Deviation Approval',
    delete_pjp = 'Delete Pjp',
    manage_rate_master = 'Manage Rate Master',
    manage_form = 'Manage Form',
    create_form = 'Create Form',
    geofence = 'Geofence',
    outlet_registration = 'Outlet Registration',
    my_profile = 'My Profile',
    loyalty_management = 'Loyalty Management'
}
